import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../auth/apiFetch';
import { getToken } from '../auth/PasswordGate';

export interface TransactionSplit {
  amount: number;
  budgetCategory: string | null;
}

export interface Transaction {
  id: number;
  accountId: number;
  accountName: string;
  accountMask: string | null;
  institutionName: string | null;
  postedAt: string; // YYYY-MM-DD
  amount: number; // Plaid sign convention: positive = debit (money out)
  currency: string | null;
  merchantName: string | null;
  name: string;
  pfcPrimary: string | null;
  pfcDetailed: string | null;
  budgetCategory: string | null;
  pending: boolean;
  splits: TransactionSplit[];
}

// Whose attribution to compute. `viewAsUserId` defaults to the caller on
// the server when omitted. Scoping to another household member returns
// empty rows for the caller (no joint accounts; the other person's
// personal accounts are privacy-hidden).
export interface TransactionsScope {
  viewAsUserId: number;
}

export interface TransactionsFilters {
  accountId?: number | null;
  from?: string | null; // YYYY-MM-DD
  to?: string | null;
  scope?: TransactionsScope;
}

export interface TransactionTotals {
  spent: number;
  received: number;
}

export type TransactionsState =
  | { status: 'loading'; transactions: []; total: 0; totals: TransactionTotals }
  | {
      status: 'ready';
      transactions: Transaction[];
      total: number;
      totals: TransactionTotals;
      reload: () => void;
    }
  | {
      status: 'error';
      transactions: [];
      total: 0;
      totals: TransactionTotals;
      error: string;
      reload: () => void;
    };

const ZERO_TOTALS: TransactionTotals = { spent: 0, received: 0 };

export function useTransactions(filters: TransactionsFilters = {}): TransactionsState {
  const [state, setState] = useState<TransactionsState>({
    status: 'loading',
    transactions: [],
    total: 0,
    totals: ZERO_TOTALS,
  });

  const filterKey = JSON.stringify(filters);

  const load = useCallback(() => {
    const token = getToken();
    if (!token) {
      setState({
        status: 'ready',
        transactions: [],
        total: 0,
        totals: ZERO_TOTALS,
        reload: load,
      });
      return;
    }
    const params = new URLSearchParams();
    if (filters.accountId != null) params.set('accountId', String(filters.accountId));
    if (filters.from) params.set('from', filters.from);
    if (filters.to) params.set('to', filters.to);
    if (filters.scope) {
      params.set('scope', 'user');
      params.set('viewAs', String(filters.scope.viewAsUserId));
    }
    // Match the server cap explicitly. The server default is also 500 now,
    // but pinning it here keeps the contract obvious — any caller that
    // wants a smaller page still has to opt down. Months that cross 500
    // txns will need a real pagination UI; today they're rare enough that
    // the cap is the right ceiling.
    params.set('limit', '500');
    apiFetch(`/api/transactions${params.toString() ? `?${params}` : ''}`)
      .then(async (res) => {
        if (!res.ok) throw new Error(`transactions ${res.status}`);
        return (await res.json()) as {
          transactions: Transaction[];
          total: number;
          totals: TransactionTotals;
        };
      })
      .then((data) => {
        setState({
          status: 'ready',
          transactions: data.transactions,
          total: data.total,
          totals: data.totals ?? ZERO_TOTALS,
          reload: load,
        });
      })
      .catch((err: Error) => {
        setState({
          status: 'error',
          transactions: [],
          total: 0,
          totals: ZERO_TOTALS,
          error: err.message,
          reload: load,
        });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey]);

  useEffect(() => {
    load();
  }, [load]);

  return state;
}
